import { createRouter, createWebHistory } from 'vue-router';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/crm/'
    },

    // ------------------- //
    //  Call Popup Path    //
    // ------------------- //

    {
      path: '/call-popup',
      component: () => import('@/ui/views/call/CallPopupView.vue'),
      meta: { id: 'call-popup', pageName: 'Call Notes' }
    },
    {
      path: '/noi-calc',
      component: () => import('@/ui/views/call/NoiCalcTablePopupView.vue'),
      meta: { id: 'noi-calc', pageName: 'NOI Calc Table' }
    },

    // ------------------- //
    //  TV Dashboard Path  //
    // ------------------- //

    {
      path: '/tv-dashboard',
      component: () => import('@/ui/views/crm/dashboard/tv-dashboard/TvDashboardTest.vue'),
      meta: { id: 'tv-dashboard', pageName: 'TV Dashboard' }
    },
    {
      path: '/tv-dashboard-full',
      component: () => import('@/ui/views/crm/dashboard/tv-dashboard/TvDashboardView.vue'),
      meta: { id: 'tv-dashboard-full', pageName: 'TV Dashboard Full' }
    },

    // ----------- //
    //  CRM Paths  //
    // ----------- //

    {
      path: '/crm',
      component: () => import('@/ui/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          redirect: '/crm/activity'
        },
        {
          path: 'dashboard',
          components: { main: () => import('@/ui/views/crm/CrmDashboardView.vue') },
          meta: { id: 'crm-dashboard', parentName: 'crm', pageName: 'crm › Dashboard', pageIcon: 'fa fa-chart-line' }
        },
        {
          path: 'dashboard-v2',
          redirect: to => ({ path: '/crm/dashboard', query: to.query })
        },
        {
          path: 'map',
          components: { main: () => import('@/ui/views/crm/CrmMapView.vue') },
          meta: { id: 'crm-map', parentName: 'crm', pageName: 'crm › Map' }
        },
        {
          path: 'data-management',
          components: { main: () => import('@/ui/views/crm/CrmDataManagementView.vue') },
          meta: { id: 'crm-data-management', parentName: 'crm', pageName: 'crm › Data Management' }
        },
        {
          path: 'add-entity',
          components: { main: () => import('@/ui/views/crm/add-entity/AddEntityWrapper.vue') },
          meta: { id: 'add-entity', parentName: 'crm', pageName: 'crm › Add Entity' }
        },
        {
          path: 'find-properties',
          components: { main: () => import('@/ui/views/crm/find-entity/FindEntityWrapper.vue') },
          meta: { id: 'find-properties', mode: 'dual', parentName: 'crm', pageName: 'crm › Find Properties' }
        },
        {
          path: 'find-properties/list',
          components: { main: () => import('@/ui/views/crm/find-entity/FindEntityWrapper.vue') },
          meta: { id: 'find-properties-list', mode: 'list', parentName: 'crm', pageName: 'crm › Find Properties › List' }
        },
        {
          path: 'find-properties/map',
          components: { main: () => import('@/ui/views/crm/find-entity/FindEntityWrapper.vue') },
          meta: { id: 'find-properties-map', mode: 'map', parentName: 'crm', pageName: 'crm › Find Properties › Map' }
        },
        {
          path: 'find-companies',
          components: { main: () => import('@/ui/views/crm/find-entity/FindEntityWrapper.vue') },
          meta: { id: 'find-companies', parentName: 'crm', pageName: 'crm › Find Companies' }
        },
        {
          path: 'find-contacts',
          components: { main: () => import('@/ui/views/crm/find-entity/find-contacts/FindContactsWrapper.vue') },
          meta: { id: 'find-contacts', mode: 'list', parentName: 'crm', pageName: 'crm › Find Contacts' }
        },
        {
          path: 'find-contacts/lookup',
          components: { main: () => import('@/ui/views/crm/find-entity/find-contacts/FindContactLookupView.vue') },
          meta: { id: 'find-contacts-lookup', parentName: 'crm', pageName: 'crm › Find 1 Contact' }
        },
        {
          path: 'entity/:entityType/:entityId',
          components: { main: () => import('@/ui/views/crm/entity-details/EntityDetailsPageWrapper.vue') },
          meta: { id: 'entity-details', parentName: 'crm', pageName: 'crm › Entity Details' }
        },
        {
          path: 'entity/:entityType/:entityId/history',
          components: { main: () => import('@/ui/views/crm/entity-details/EntityHistoryPageWrapper.vue') },
          meta: { id: 'entity-history', parentName: 'crm', pageName: 'crm › Entity History' }
        },
        {
          path: 'buyer-needs',
          components: { main: () => import('@/ui/views/crm/buyer-needs/CrmBuyerNeedsView.vue') },
          meta: { id: 'crm-buyer-needs', parentName: 'crm', pageName: 'crm › Buyer Needs' }
        },
        {
          path: 'activity',
          components: { main: () => import('@/ui/views/crm/CrmActivityView.vue') },
          meta: { id: 'crm-activity', parentName: 'crm', pageName: 'crm › Activity' }
        },
        {
          path: 'calendar',
          components: { main: () => import('@/ui/views/crm/CrmCalendarView.vue') },
          meta: { id: 'crm-calendar', parentName: 'crm', pageName: 'crm › Calendar' }
        },
        {
          path: 'loi-generator',
          components: { main: () => import('@/ui/views/crm/CrmLoiGeneratorView.vue') },
          meta: { id: 'crm-loi-generator', parentName: 'crm', pageName: 'crm › LOI Generator' }
        },
        {
          path: 'mark-ai',
          components: { main: () => import('@/ui/views/crm/CrmMarkAiView.vue') },
          meta: { id: 'crm-mark-ai', parentName: 'crm', pageName: 'crm › Mark AI' }
        },
        // hidden for now but because it was belonging to dashboard v1 and we deprecated v1
        // {
        //   path: 'market-snapshots',
        //   components: { main: () => import('@/ui/views/crm/crm-market-snapshots/CrmMarketSnapshotsView.vue') },
        //   meta: { id: 'crm-market-snapshots', parentName: 'crm', pageName: 'crm › Market Snapshots' }
        // },
        {
          path: 'aloware-playground',
          components: { main: () => import('@/ui/views/crm/crm-aloware-playground/AlowarePlaygroundView.vue') },
          meta: { id: 'crm-aloware-playground', parentName: 'crm', pageName: 'crm › Aloware Playground' }
        }

      ]
    },

    // ----------------- //
    //  Providers Paths  //
    // ----------------- //

    {
      path: '/summit',
      component: () => import('@/ui/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          components: { main: () => import('@/ui/views/summit/SummitView.vue') }, 
          meta: { id: 'summit-summit', parentName: 'summit', pageName: 'Summit › Summit' }
        },
        {
          path: 'external-listings',
          components: { main: () => import('@/ui/views/summit/external-listings/ExternalListingsView.vue') },
          meta: { id: 'summit-external-listings', parentName: 'summit', pageName: 'Summit › External Listings' }
        },
        {
          path: 'lat-long-review',
          components: { main: () => import('@/ui/views/summit/latlong-review/LatLongReviewWrapper.vue') },
          meta: { id: 'summit-lat-long-review', parentName: 'summit', pageName: 'Summit › Lat & Long Review' }
        },
        {
          path: 'chain-matcher',
          components: { main: () => import('@/ui/views/summit/chain-matcher/ChainMatcherView.vue') },
          meta: { id: 'summit-chain-matcher', parentName: 'summit', pageName: 'Summit › Chain Matcher' }
        },
        {
          path: 'chain-matcher-package',
          components: { main: () => import('@/ui/views/summit/chain-matcher/ChainMatcherView.vue') },
          meta: { id: 'summit-chain-matcher-package', parentName: 'summit', pageName: 'Summit › Chain Matcher (Package)' }
        },
        {
          path: 'unmatched-reviewer',
          components: { main: () => import('@/ui/views/summit/unmatched-reviewer/UnmatchedReviewerWrapper.vue') },
          meta: { id: 'summit-unmatched-reviewer', parentName: 'summit', pageName: 'Summit › Unmatched Reviewer' }
        },
        {
          path: 'unscrubbed',
          components: { main: () => import('@/ui/views/summit/unscrubbed/UnscrubbedWrapper.vue') },
          meta: { id: 'summit-unscrubbed', parentName: 'summit', pageName: 'Summit › Unscrubbed' }
        },
        {
          path: 'chain-manager',
          components: { main: () => import('@/ui/views/summit/chain-tracker/ChainTrackerView.vue') },
          meta: { id: 'summit-chain-manager', parentName: 'summit', pageName: 'Summit › Chain Manager' }
        },
        {
          path: 'landvision-rescue',
          components: { main: () => import('@/ui/views/summit/landvision-rescue/LandVisionRescueView.vue') },
          meta: { id: 'summit-landvision-rescue', parentName: 'summit', pageName: 'Summit › LandVision Rescue' }
        },
        {
          path: 'custom-attributes',
          components: { main: () => import('@/ui/views/summit/custom-attributes/CustomAttributesManagerView.vue') },
          meta: { id: 'summit-custom-attributes', parentName: 'summit', pageName: 'Summit › Custom Attributes' }
        },

      ]
    },

    // -------------- //
    //  System Paths  //
    // -------------- //

    {
      path: '/system',
      component: () => import('@/ui/layouts/MainLayout.vue'),
      children: [
        {
          path: '',
          redirect: '/system/users',
        },
        {
          path: 'notifications',
          components: { main: () => import('@/ui/views/system/notifications/SystemNotificationsView.vue') },
          meta: { id: 'system-notifications', parentName: 'system', pageName: 'System › Notifications' }
        },
        {
          path: 'export-logs',
          components: { main: () => import('@/ui/views/system/export-log/SystemExportLogAdminView.vue') },
          meta: { id: 'system-export-logs', parentName: 'system', pageName: 'System › Export Logs' }
        },
        {
          path: 'users',
          components: { main: () => import('@/ui/views/system/users/SystemUsersView.vue') },
          meta: { id: 'system-users', parentName: 'system', pageName: 'System › Users' }
        },
        {
          path: 'user-tracking',
          components: { main: () => import('@/ui/views/system/user-tracking/SystemUserTrackingView.vue') },
          meta: { id: 'system-user-tracking', parentName: 'system', pageName: 'System › User Tracking' }
        },
        {
          path: 'automation-jobs',
          components: { main: () => import('@/ui/views/system/automation-jobs/SystemAutomationJobsView.vue') },
          meta: { id: 'system-automation-jobs', parentName: 'system', pageName: 'System › Automation Jobs' }
        },
        {
          path: 'websocket-receiver',
          components: { main: () => import('@/ui/views/system/websocket-receiver/WebSocketReceiverView.vue') },
          meta: { id: 'system-websocket-receiver', parentName: 'system', pageName: 'System › WebSocket Receiver' }
        },
        {
          path: 'sectors',
          components: { main: () => import('@/ui/views/system/sectors/SystemSectorsView.vue') },
          meta: { id: 'system-sectors', parentName: 'system', pageName: 'System › Sectors' }
        },
        {
          path: 'nnn-categories',
          redirect: '/system/sectors'
        },
        {
          path: 'public-records',
          components: { main: () => import('@/ui/views/system/public-records/SystemPublicRecordsView.vue') },
          meta: { id: 'system-public-records', parentName: 'system', pageName: 'System › Public Records' }
        },
        {
          path: 'counties',
          redirect: { path: '/system/public-records', query: { tab: 'counties' } }
        },
        // ------------------------------- //
        //  DEV-Only Routes                //
        // ------------------------------- //

        ...(import.meta.env.DEV ? [{
          path: 'nnn-icons',
          components: { main: () => import('@/ui/views/system/nnn-icons/SystemNNNIconsView.vue') },
          meta: { id: 'system-nnn-icons', parentName: 'system', pageName: 'System › NNN Icons' }
        }, {
          path: 'loader-bench',
          components: { main: () => import('@/ui/views/system/loader-bench/LoaderBenchView.vue') },
          meta: { id: 'system-loader-bench', parentName: 'system', pageName: 'System › Loader Bench' }
        }] : []),
        
      ]
    }
  ]


})

router.afterEach((to) => {
  const pageName = to.meta.pageName ? to.meta.pageName : ''
  
  if (pageName) {
    document.title = `Slingshot CRE | ${pageName}`
  } else {
    document.title = 'Slingshot CRE'
  }
  
})

// ---- //
//  Handle Dynamic Import Failures  //
// ---- //

router.onError((error, to) => {
  console.error('Router error:', error)
  
  // Handle dynamic import failures (common in production after deployments)

  if (error.message && (
    error.message.includes('error loading dynamically imported module') ||
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading chunk')
  )) {
    console.warn('Dynamic import failed for route:', to.fullPath, error.message)
    
    // Only reload if we haven't already tried (prevent infinite loops)

    if (!sessionStorage.getItem('dynamic-import-reload-attempted')) {
      sessionStorage.setItem('dynamic-import-reload-attempted', 'true')
      console.warn('Attempting page reload to clear cache...')
      
      // Clear the flag after a delay to allow future reloads if needed

      setTimeout(() => {
        sessionStorage.removeItem('dynamic-import-reload-attempted')
      }, 5000)
      
      window.location.href = to.fullPath
    } else {
      console.error('Dynamic import failed after reload attempt, not retrying to prevent loop')
    }
  }
})

// ------------------------------------------------- //
//                                                   //
//    Route Prefetch                                 //
//    Preloads route chunks on hover + idle          //
//                                                   //
// ------------------------------------------------- //

const prefetchedRoutes = new Set<string>()

// ------------------------------- //
//  Prefetch Route Chunk           //
// ------------------------------- //

export function prefetchRoute(path: string) {
  if (prefetchedRoutes.has(path)) return
  prefetchedRoutes.add(path)

  const resolved = router.resolve(path)
  if (resolved.matched.length > 0) {
    for (const match of resolved.matched) {
      const components = match.components || {}
      for (const comp of Object.values(components)) {
        if (typeof comp === 'function') {
          (comp as () => Promise<any>)().catch(() => {
            prefetchedRoutes.delete(path)
          })
        }
      }
    }
  }
}

// ------------------------------- //
//  Background Prefetch (idle)     //
// ------------------------------- //

router.isReady().then(() => {
  const keyRoutes = [
    '/crm/add-entity',
    '/crm/find-properties',
    '/crm/find-companies',
    '/crm/find-contacts',
    '/crm/activity',
    '/summit/unmatched-reviewer'
  ]

  const doPrefetch = () => {
    keyRoutes.forEach(path => prefetchRoute(path))
  }

  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(doPrefetch)
  } else {
    setTimeout(doPrefetch, 2000)
  }
})

export default router
